import {Box, CircularProgress, Divider, IconButton, InputAdornment, TextField, Typography} from '@mui/material'
import {Send} from '@mui/icons-material'
import {useLocation, useParams} from 'react-router-dom'
import {useRef, useState, useEffect} from 'react'
import {useGetNotification, useGetOrder} from '../../logic/useApiQuery'
import { useReplyNotification, useMarkReplyRead } from '../../logic/useApiMutation'
import { useOptimisticUpdateNotification, useOptimisticUpdateOrderNotification } from '../../logic/optimisticUpdates'
import {LoadingScreen} from '../shared'
import {SanitizedHtml} from '../shared/SanitizedHtml'
import {PortalNotification, PortalNotificationEvent} from '../../types'
import {hasUnreadReplies} from '../../utils'
import dayjs from 'dayjs'

function MessageBubble({ content, date, isOurs, isRead, allowHtml }: { content: string; date: string; isOurs: boolean; isRead?: boolean; allowHtml?: boolean }) {
  if (isOurs) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Box
          sx={{
            maxWidth: '85%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '16px 4px 16px 16px',
            p: '12px 16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
          }}
        >
          {allowHtml ? <SanitizedHtml html={content}/> : <span>{content}</span>}
        </Box>
        <Typography variant="teeny" sx={{ color: '#ABABAF', mt: 0.75, mr: 0.5, display: 'block' }}>
          You - {date} · <Box component="span" sx={{ color: isRead ? 'success.main' : 'inherit' }}>{isRead ? 'Read ✓' : 'Sent'}</Box>
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ maxWidth: '85%' }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '4px 16px 16px 16px',
            p: '12px 16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            wordBreak: 'break-word',
          }}
        >
          {allowHtml ? <SanitizedHtml html={content}/> : <span>{content}</span>}
        </Box>
        <Typography variant="teeny" sx={{ color: '#ABABAF', mt: 0.75, ml: 0.5, display: 'block' }}>
          Title Team - {date}
        </Typography>
      </Box>
    </Box>
  )
}

const MAX_MESSAGE_LENGTH = 1000

export function MessageThreadPage() {
  const { closingId, notificationId: notificationIdParam, id } = useParams()
  const notificationId = notificationIdParam ?? id
  const {search} = useLocation()
  const [replyText, setReplyText] = useState('')

  const {data: orderData} = useGetOrder(closingId ?? '', search ?? '')
  const cachedRef = orderData?.Notifications?.find(n => n?.NotificationId === notificationId)

  const placeholderData: PortalNotification | undefined = cachedRef
    ? {
      Title: cachedRef.NotificationTitle,
      CreatedDate: cachedRef.CreatedDateTime,
      CreatedDateTime: cachedRef.CreatedDateTime,
      Content: cachedRef.Content ?? '',
      NotificationId: cachedRef.NotificationId,
      ReadReceipt: cachedRef.ReadReceipt,
      Type: cachedRef.Type,
      Replies: cachedRef.Replies,
      UserOrderKey: '',
      CommunicationId: '',
      ProcessIdStepIdCommunicationId: '',
      CompanyId: '',
      OrderId: '',
      Role: '',
    }
    : undefined

  const {data: notification, isLoading, error} = useGetNotification({
    notificationId,
    queryOptions: {placeholderData},
  })

  const {optimisticUpdateNotification} = useOptimisticUpdateNotification()
  const {optimisticUpdateOrderNotification} = useOptimisticUpdateOrderNotification()

  const {mutate: markReplyRead} = useMarkReplyRead(
    closingId ?? '',
    notificationId ?? '',
    search,
    {
      onSuccess: () => {
        const withRepliesMarkedRead = (current: {Replies?: PortalNotificationEvent[]}) => ({
          ReadReceipt: true,
          Replies: (current.Replies ?? []).map(reply =>
            reply.Source === 'liveLook'
              ? {...reply, AdditionalData: {...reply.AdditionalData, readStatus: true}}
              : reply
          ),
        })
        optimisticUpdateNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: withRepliesMarkedRead,
        })
        optimisticUpdateOrderNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: withRepliesMarkedRead,
        })
      },
    },
  )

  // Mark as read on mount if not already read, or if there are unread replies. Use a ref so we
  // only fire once even if the notification data changes (e.g. placeholder → real data).
  const markedReadRef = useRef(false)
  useEffect(() => {
    if (!markedReadRef.current && notification && (!notification.ReadReceipt || hasUnreadReplies(notification))) {
      markedReadRef.current = true
      markReplyRead(undefined)
    }
  }, [notification, markReplyRead])

  const {mutate: sendReply, isLoading: isSending, error: sendError} = useReplyNotification(
    closingId ?? '',
    notificationId ?? '',
    search,
    {
      onSuccess: (_data, variables) => {
        const optimisticReply: PortalNotificationEvent = {
          UserOrderKeyNotificationId: '',
          CreatedDate: new Date().toISOString(),
          AuthProviderSub: '',
          CompanyIdOrderIdHash: '',
          NotificationId: notificationId ?? '',
          Type: 'read',
          UserOrderKey: '',
          AdditionalData: {message: variables.message},
        }
        const appendReply = (current: {Replies?: PortalNotificationEvent[]}) => ({
          Replies: [...(current.Replies ?? []), optimisticReply],
        })
        optimisticUpdateNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: appendReply,
        })
        optimisticUpdateOrderNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: appendReply,
        })
        setReplyText('')
      },
    },
  )

  const replies = notification?.Replies ?? []

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!replyText.trim() || isSending) return
    sendReply({message: replyText.trim()})
  }

  if (isLoading) return <LoadingScreen/>
  if (error) throw error
  if (!notification) return null

  const baseDate = dayjs(notification.CreatedDate).format('MMMM D, YYYY [at] h:mm A')

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '750px'}}>
      <Typography
        variant="h5"
        sx={{fontWeight: 500, lineHeight: '22px', mb: 2, wordBreak: 'break-word'}}
      >
        {notification.Title}
      </Typography>
      <Divider sx={{mb: 3}}/>

      {/* Messages */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pb: '80px'}}>
        {notification.Content && (
          <MessageBubble content={notification.Content} date={baseDate} isOurs={false} allowHtml/>
        )}

        {replies.map((reply) => (
          <MessageBubble
            key={reply.CreatedDate}
            content={typeof reply.AdditionalData?.message === 'string' ? reply.AdditionalData.message : ''}
            date={dayjs(reply.CreatedDate).format('MMMM D, YYYY [at] h:mm A')}
            isOurs={reply.Source !== 'liveLook'}
            isRead={reply.AdditionalData?.readStatus === true}
          />
        ))}
      </Box>

      {/* Reply input */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: 'fixed',
          bottom: { xs: '56px', sm: 0 },
          left: { xs: 0, sm: '270px' },
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: 'white',
          boxShadow: '0 -1px 4px rgba(0,0,0,0.1)',
          zIndex: 10,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          disabled={isSending}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          error={!!sendError}
          helperText={sendError ? 'Failed to send. Please try again.' : `${MAX_MESSAGE_LENGTH - replyText.length} characters`}
          inputProps={{maxLength: MAX_MESSAGE_LENGTH}}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Send reply"
                  type="submit"
                  color="primary"
                  disabled={!replyText.trim() || isSending}
                  edge="end"
                >
                  {isSending ? <CircularProgress size={24} color="primary"/> : <Send/>}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{'& .MuiOutlinedInput-root': {borderRadius: '12px'}}}
        />
      </Box>
    </Box>
  )
}
